import React, {useEffect, useState} from "react";
import {useAuth} from "../../context/authContext";
import {useHistory, useLocation} from "react-router-dom";
import PropTypes from "prop-types";
import {getAllObjsByPath, getLandingPageURL, getPathToObj} from "../../utils/util";
import {useDispatch, useSelector} from "react-redux";
import {LocalStorageUtil, SessionStorageUtil} from "../../utils/localStorageUtil";
import {Button} from "react-bootstrap";
import {getProperties} from "../../actions/constantsActions";

const componentName = "Navigation";
const CE_RESTORE_OBJ = "ce_restore_obj";

const SubTitle = ({ initialValue, getData }) => {
  const [value, setValue] = useState(initialValue);
  useEffect(() => {
    getData().then((data) => {
      setValue(data);
    });
  }, [getData]);

  return (
    <span className="badge badge-pill bg-secondary text-white float-end">
      {value}
    </span>
  );
};

SubTitle.propTypes = {
  initialValue: PropTypes.string.isRequired,
  getData: PropTypes.func.isRequired,
};

const getCurrentPath = (currentPath) => currentPath;

const deriveIsOpenAndActive = (menuItem, currentPath, level) => {
  return (
    menuItem.isOpen ||
    (currentPath &&
      level < currentPath.length &&
      currentPath[level] === menuItem)
  );
};

const MenuList = ({ menuItems, level, currentPath, setActiveMenu, activeMenu, propertiesToSideBar, index }) => {
  const auth = useAuth();
  const history = useHistory();
  const dispatch = useDispatch();

  const onClickMenuItem = (menuItem) => {
    if (menuItem.to) {
      setActiveMenu(menuItem.id);
      history.push(menuItem.to);
    }
    if (menuItem.onClick) {
      if (menuItem.useDispatch) {
        dispatch(menuItem.onClick());
      } else {
        menuItem.onClick();
      }
    }
  };

  return menuItems
    .filter((menuItem) => {
      if (!menuItem?.canAccess?.(auth)) return false;

      if (menuItem.title === "Care Management") {
        return menuItem?.items?.some(sub => sub?.canAccess?.(auth));
      }

      if (menuItem.title === "Schedule") {
        return propertiesToSideBar["react.properties.scheduleQL.feature.enabled"] === true;
      }

      return true;
    })
    .map((menuItem, index) => {
      const isStateful = menuItem.useDispatch;
      const hasCustomComponent = menuItem.items || menuItem.onSelect || menuItem.onLoad;

      if (hasCustomComponent) {
        return isStateful ? (
          <StatefulMenu
            key={`${componentName}-${menuItem.id}-navItem-${level}-${index}`}
            menuItem={menuItem}
            level={level}
            currentPath={currentPath}
            index={index}
            setActiveMenu={setActiveMenu}
            activeMenu={activeMenu}
          />
        ) : (
          <Menu
            key={`${componentName}-${menuItem.id}-navItem-${level}-${index}`}
            setActiveMenu={setActiveMenu}
            activeMenu={activeMenu}
            menuItem={menuItem}
            level={level}
            currentPath={currentPath}
            index={index}
          />
        );
      }

      return (
        <li
          key={`${componentName}-${menuItem.id}-navItem-${level}-${index}`}
          id={`${componentName}-${menuItem.id}-navItem-${level}-${index}`}
          data-active={deriveIsOpenAndActive(menuItem, currentPath, level) || null}
          data-level={level}
          className={`nav-item ${menuItem.id === activeMenu ? "active" : ""}`}
        >
          <Button variant="link" id={`${componentName}-${menuItem.id}-${index}`} onClick={() => onClickMenuItem(menuItem)}
            className={`nav-link py-3 d-flex justify-content-start align-items-center gap-4 w-100 text-start ${menuItem.id === activeMenu ? "active" : "text-dark"}`}
            type="button" aria-label={`Navigate To ${menuItem.title}`}>
            {menuItem.icon && <i className={`fa-regular ${menuItem.icon}`} />}
            <span className="nav-main">{menuItem.title}</span>
          </Button>
          {menuItem.subTitle && (
            <SubTitle
              className="sub-title"
              initialValue="..."
              getData={() => menuItem.subTitle()}
            />
          )}
        </li>
      );
    });
};

const Menu = ({ menuItem, level, currentPath, activeMenu, setActiveMenu, index }) => {
  const history = useHistory();
  const [isOpen, setIsOpen] = useState(() =>
    deriveIsOpenAndActive(menuItem, currentPath, level)
  );

  const openCloseMenu = (menuItem) => {
    if (menuItem.to) {
      history.push(menuItem.to);
      setActiveMenu(menuItem.id);
    }
  };

  return (
    <>
      {/* Menu Header Dropdown */}
      <li key={`${componentName}-${menuItem.id}-navHeader-${level}-${index}`} id={`${componentName}-${menuItem.id}-navHeader-${level}-${index}`} className={menuItem.id === activeMenu ? "active" : null}>
        <div className={`d-flex justify-content-between align-items-center ${menuItem.id === activeMenu ? "active" : null}`}>
          <Button variant="link" id={`${componentName}-menuHeader-${menuItem.id}`} onClick={() => openCloseMenu(menuItem)}
            className="nav-link py-3 d-flex justify-content-between align-items-center gap-3 w-100"
            type="button" aria-label={`Open ${menuItem.title} Menu`}>
            <span className="d-flex justify-content-start align-items-center gap-4 w-100">
                {menuItem.icon && <i id={`${componentName}-menuIcon-${menuItem.id}`} className={`fa-regular ${menuItem.icon}`} />}
                <span id={`${componentName}-${menuItem.id}`}>{menuItem.title}</span>
                {menuItem.subTitle && (
                  <SubTitle
                    className="sub-title"
                    initialValue="..."
                    getData={() => menuItem.subTitle()}
                  />
                )}
            </span>
          </Button>
          <Button variant="link" id={`${componentName}-menuArrow-${menuItem.id}`} className={`fa-regular text-decoration-none me-3 p-0 ${isOpen ? 'fa-chevron-down' : 'fa-chevron-right'}`}
            onClick={() => setIsOpen(!isOpen)} type="button" tabIndex={0} onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                setIsOpen(!isOpen);
              }
            }} aria-label={`Toggle ${menuItem.title} Submenu`} />
        </div>
        {isOpen && (
          <ul key={`${componentName}-${menuItem.id}-list-${level}-${index}`} id={`${componentName}-${menuItem.id}-list-${level}-${index}`} className="collapse show nav flex-column bg-light ps-5" data-bs-parent={`#${componentName}-menuHeaderOpen-${menuItem.id}`}>
            <MenuList
              menuItems={menuItem.items}
              level={level + 1}
              currentPath={currentPath}
              setActiveMenu={setActiveMenu}
              activeMenu={activeMenu}
              index={index}
            />
          </ul>
        )}
      </li>
    </>
  );
};

Menu.propTypes = {
  menuItem: PropTypes.shape({
    id: PropTypes.string.isRequired,
    icon: PropTypes.string,
    title: PropTypes.string.isRequired,
    subTitle: PropTypes.func,
    to: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.object,
    ]),
    items: PropTypes.array,
  }).isRequired,
  level: PropTypes.number.isRequired,
  currentPath: PropTypes.array.isRequired,
  activeMenu: PropTypes.string.isRequired,
  setActiveMenu: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
};


const StatefulMenu = ({ menuItem, level, currentPath, activeMenu, setActiveMenu, index }) => {
  const menuItemData = useSelector(
    (reduxState) => reduxState[menuItem.reducerName]
  );
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(menuItem.onLoad());
  }, [dispatch, menuItem]);

  let menuItems = menuItemData;

  if (menuItem.filterMenuItems) {
    menuItems = menuItem.filterMenuItems(menuItems);
  }

  return (
    <>
      {menuItems ? (
        <MenuList
          menuItems={menuItems}
          level={level + 1}
          currentPath={currentPath}
          activeMenu={activeMenu}
          setActiveMenu={setActiveMenu}
        />
      ) : (
        <div></div>
      )}
    </>
  );
};

StatefulMenu.propTypes = {
  menuItem: PropTypes.shape({
    id: PropTypes.string.isRequired,
    reducerName: PropTypes.string.isRequired,
    onLoad: PropTypes.func.isRequired,
    filterMenuItems: PropTypes.func,
  }).isRequired,
  level: PropTypes.number.isRequired,
  currentPath: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
  ]).isRequired,
  activeMenu: PropTypes.string.isRequired,
  setActiveMenu: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
};

const Navigation = ({ menuItems }) => {
  const auth = useAuth();
  const history = useHistory();
  const location = useLocation();
  const [activeMenu, setActiveMenu] = useState('');
  const [propertiesToSideBar, setPropertiesToSideBar] = useState({});
  const currentPath = getCurrentPath(location.pathname);
  const landingPage = auth.getLandingPage();

  useEffect(() => {
    const restoreObj = LocalStorageUtil.getObj(CE_RESTORE_OBJ);
    if (location.pathname === "/" && !restoreObj) {
      history.push(getLandingPageURL(landingPage));
    }
  }, [history, landingPage, location.pathname]);

  const identityKey = auth?.userImpersonationActive?.()
  ? `imp:${auth.getImpersonationTargetUsername?.() || ""}`
  : `self:${auth.getUsername?.() || ""}`;

  const isPathMatch = (urlPattern, url) => {
    const urlParts = url.split("uuid");
    if (!url.startsWith("/#!") && urlParts && urlParts.length === 2) {
      url = urlParts[0] + "uuid" + urlParts[1];
    }
    return urlPattern === url;
  };

  const currentNavPath = getPathToObj(menuItems, (propertyName, level, obj) =>
    isPathMatch(obj.to, currentPath)
  );

  let currentNavPathObjs = [];
  if (currentNavPath?.length) {
    currentNavPathObjs = getAllObjsByPath(menuItems, currentNavPath[0]);
  }

  useEffect(() => {
    initProperties()
  }, [])

  const initProperties = async () => {
    let res = await getProperties();
    SessionStorageUtil.setObj("properties", res.data);
    if (res.data) {
      setPropertiesToSideBar(res.data)
    }
  }

  useEffect(() => {
    setActiveMenu('');
    setPropertiesToSideBar({});
  }, [identityKey]);

  return (
    <ul key={identityKey} className="nav nav-pills flex-column mb-sm-auto mt-4 mb-0 align-items-center align-items-sm-start">
      <MenuList
        menuItems={menuItems}
        level={0}
        currentPath={currentNavPathObjs}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
        propertiesToSideBar={propertiesToSideBar}
      />
    </ul>
  );
};

Navigation.propTypes = {
  menuItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      icon: PropTypes.string,
      subTitle: PropTypes.func,
      to: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      items: PropTypes.array,
      useDispatch: PropTypes.bool,
      onSelect: PropTypes.func,
      onLoad: PropTypes.func,
      canAccess: PropTypes.func,
    })
  ).isRequired,
};


export default Navigation;
